'use client'

import { useMemo, useState } from 'react'
import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { Separator } from '@cocoonspace/ui/components/separator'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@cocoonspace/ui/components/tabs'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Plural, useLingui } from '@lingui/react/macro'
import { CalendarIcon, ChevronDownIcon, XIcon } from 'lucide-react'

import {
	DrawerPopover,
	DrawerPopoverClose,
	DrawerPopoverContent,
	DrawerPopoverTrigger,
} from '~/components/drawer-dialog'
import { MultiDate, SingleDate } from './date-pickers'
import type { QueryFilters } from '../use-search-filters.hook'

export const getDayValueAsArray = (value: string[] | string | undefined) => {
	if (!value) return undefined

	return Array.isArray(value) ? value : [value]
}

export type Timeslot = Pick<QueryFilters, 'day' | 'start' | 'end'>

export interface TimeslotFilterProps {
	value: Timeslot
	onChange: (timeslot: Timeslot) => void
	className?: string
}

export type TimeslotTab = 'hour' | 'day'

const getNextTimeSlot = () => {
	const currentTime = dateTimeUtils.utils.formatToNumberTime(dateTimeUtils())
	const defaultDay = dateTimeUtils().customFormat('dateTech')
	const defaultStart = searchConfig.timeSlots.find(
		(time, i, timeSlots) =>
			dateTimeUtils.utils.convertToNumberTime(time) >= currentTime &&
			i < timeSlots.length - 1,
	)

	const defaultEnd = defaultStart
		? searchConfig.timeSlots.find(
				(time) =>
					dateTimeUtils.utils.convertToNumberTime(time) -
						dateTimeUtils.utils.convertToNumberTime(defaultStart) ===
					100,
			)
		: undefined

	return {
		day: [defaultDay],
		end: defaultEnd,
		start: defaultStart,
	} as Timeslot
}

const TimeslotFilterContent = ({ value, onChange }: TimeslotFilterProps) => {
	const { t } = useLingui()

	const [selectedTab, setSelectedTab] = useState<TimeslotTab>(
		!!value?.day && !value?.start && !value?.end ? 'day' : 'hour',
	)

	const [timeSlot, setTimeSlot] = useState<Timeslot>(() =>
		!value?.day && !value?.start && !value?.end ? getNextTimeSlot() : value,
	)

	const reset = () => {
		const value = {
			day: null,
			end: null,
			start: null,
		}

		onChange(value)
		setTimeSlot(value)
	}

	const isComplete =
		selectedTab === 'day'
			? !!timeSlot.day?.length
			: !!timeSlot.day &&
				!!timeSlot.start &&
				!!timeSlot.end &&
				dateTimeUtils.utils.convertToNumberTime(timeSlot.start) <
					dateTimeUtils.utils.convertToNumberTime(timeSlot.end)

	const handleTabChange = (tab: string) => {
		reset()
		setSelectedTab(tab as TimeslotTab)
	}

	return (
		<>
			<Tabs
				onValueChange={(tab) => {
					reset()
					handleTabChange(tab)
				}}
				value={selectedTab}
			>
				<TabsList
					className='px-4'
					variant='material'
				>
					<TabsTrigger
						value='hour'
						variant='material'
					>
						{t`À l'heure`}
					</TabsTrigger>

					<TabsTrigger
						value='day'
						variant='material'
					>
						{t`À la journée`}
					</TabsTrigger>
				</TabsList>

				<Separator />

				<TabsContent
					className='px-4'
					value='hour'
				>
					<SingleDate
						className='pt-4'
						isComplete
						onChange={setTimeSlot}
						value={timeSlot}
					/>
				</TabsContent>

				<TabsContent
					className='px-4'
					value='day'
				>
					<MultiDate
						className='pt-4'
						isComplete
						onChange={setTimeSlot}
						value={timeSlot}
					/>
				</TabsContent>
			</Tabs>

			<Separator />

			<div className='flex items-center gap-x-2 p-4 pt-0'>
				{selectedTab === 'day' && (
					<p className='text-sm'>
						<Plural
							value={timeSlot.day?.length ?? 0}
							one='Vous avez sélectionné # jour'
							other='Vous avez sélectionné # jours'
						/>
					</p>
				)}

				<div className='ml-auto flex gap-x-4'>
					<DrawerPopoverClose asChild>
						<Button
							data-testid='cancel-btn'
							size='sm'
							variant='link'
						>
							{t`Annuler`}
						</Button>
					</DrawerPopoverClose>

					<DrawerPopoverClose asChild>
						<Button
							data-testid='validate-btn'
							disabled={!isComplete}
							onClick={() => {
								onChange(timeSlot)
							}}
							size='sm'
							variant='outline'
						>
							{t`Valider`}
						</Button>
					</DrawerPopoverClose>
				</div>
			</div>
		</>
	)
}

export const TimeslotFilter = ({
	value,
	onChange,
	className,
}: TimeslotFilterProps) => {
	const { t } = useLingui()
	const [isOpen, setIsOpen] = useState(false)

	const humanReadableDate = useMemo(() => {
		if (!value?.day || !value?.day?.length) return ''

		const days = getDayValueAsArray(value?.day)?.map((day) =>
			dateTimeUtils(day).format('D MMM'),
		)

		if (value?.start && value?.end) {
			const start = value?.start?.replace(':', 'h')
			const end = value?.end?.replace(':', 'h')

			return `${days?.[0]}, ${start}-${end}`
		}

		return `${days?.join(', ')}`
	}, [value])

	const reset = () => {
		onChange({
			day: null,
			end: null,
			start: null,
		})
	}

	return (
		<DrawerPopover
			onOpenChange={setIsOpen}
			open={isOpen}
		>
			<Card
				variant='outline'
				className={cn(
					'flex h-[32px] flex-row items-center overflow-hidden rounded text-sm',
					className,
				)}
			>
				<DrawerPopoverTrigger
					className='flex flex-1 flex-row items-center gap-2 overflow-hidden p-2'
					data-testid='date-filter'
				>
					<CalendarIcon className='text-primary' />

					<div className='flex-1 truncate overflow-hidden text-left'>
						{humanReadableDate || t`Date`}
					</div>

					{!humanReadableDate && <ChevronDownIcon strokeWidth={1} />}
				</DrawerPopoverTrigger>

				{!!humanReadableDate && (
					<button
						className='pr-2'
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							reset()
							setIsOpen(false)
						}}
						type='button'
						aria-label={t`Effacer le filtre date`}
					>
						<XIcon className='text-primary' />
					</button>
				)}
			</Card>

			<DrawerPopoverContent
				className='flex w-full flex-col gap-y-4 p-0 md:w-[550px]'
				sideOffset={10}
			>
				<TimeslotFilterContent
					onChange={onChange}
					value={value}
				/>
			</DrawerPopoverContent>
		</DrawerPopover>
	)
}
